import { forkJoin as observableForkJoin, Observable, of as observableOf, of } from 'rxjs';

import { switchMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { GlobalConfig } from '../configs/GlobalConfig';
import { HttpRepository } from './http.repository';
import { User } from '../interfaces/rest/User';
import { UserRole } from '../interfaces/rest/UserRole';
import { PlantAccess } from '../interfaces/rest/PlantAccess';
import { LineBarAccessConfig } from '../interfaces/rest/LineBarAccessConfig';
import { FileMetaData } from '../interfaces/rest/FileMetaData';
import { UserGeographicalAssignmentTypes } from '../interfaces/rest/UserGeographicalAssignmentTypes';
import { Region } from '../interfaces/rest/Region';
import { HttpClient } from "@angular/common/http";
import {PagedData} from "../interfaces/rest/paging/PagedData";
import {PagingRequest} from "../interfaces/rest/paging/PagingRequest";
import {UserSessionRepository} from "./user-session.repository";
import {Filter} from "../interfaces/rest/paging/Filter";

@Injectable()
export class UserRepository {

    constructor(protected http: HttpClient,
                protected httpRepository: HttpRepository,
                protected userSessionRepository: UserSessionRepository) {

    }

    /**
     * Create user body data for form submission.
     * @param user
     * @param {boolean} editPermission
     * @param {Array<UserRole>} relevantRoles
     * @returns {User}
     */
    createUserBody(user: any, editPermission: boolean, relevantRoles: Array<UserRole>) {
        let body: any = {};

        body.firstName = user.firstName;
        body.lastName = user.lastName;
        body.email = user.email;
        body.dateFormat = user.dateConfig;
        body.timeFormat = user.timeConfig;
        if (user.password) {
            body.password = user.password;
        }
        body.plantAccess = [];
        if (user.plant && user.plant.length > 0) {
            user.plant.forEach((plant: any) => {
                let plantAccess: PlantAccess = new PlantAccess();
                plantAccess.plantId = plant;
                body.plantAccess.push(plantAccess);
            });
        }

        body.lineBarAccess = [];

        if (user.line && user.line.length > 0) {
            user.line.forEach((line: any) => {
                let lineBarAccessConfig: LineBarAccessConfig = new LineBarAccessConfig();
                lineBarAccessConfig.lineId = line;
                body.lineBarAccess.push(lineBarAccessConfig);
            });
        }

        if (user.client && (typeof user.client === 'number' || typeof user.client === 'string')) {
            User.setClient(body, user.client);
        }

        if (user.roles) {
            user.roles.forEach((role: any) => {
                let relevantRole = relevantRoles.find(r => r.keyName === role.target_id);
                if (relevantRole) {
                    body.userRole = relevantRole;
                }
            });
        }

        if (user.language) {
            body.languageId = Number(user.language);
        } else {
            body.languageId = 1;
        }

        if (user.type === 'subuser') {
            body.isSee = false;
        } else {
            body.isSee = true;
        }

        body.phoneNumber = user.mobilePhoneNumber;
        body.title = user.title;

        User.setEmailNotificationEnabled(body, user.notificationPreference.email);
        User.setSmsNotificationEnabled(body, user.notificationPreference.sms);

        if (user.avatar) {
            let file: FileMetaData = new FileMetaData();
            file.fileId = user.avatar;
            body.profileImage = file;
        }
        else {
            body.profileImage = null;
            body.removeProfileImage = true;
        }

        if (body.isSee) {
            let userGeographicalAssignment: Region = new Region();
            userGeographicalAssignment.isCustom = false;

            if (user.regions === 'area') {
                userGeographicalAssignment.userGeographicalAssignmentType = UserGeographicalAssignmentTypes.CustomCountryList;
                userGeographicalAssignment.isCustom = true;
                userGeographicalAssignment.countryList = user.countries;
            } else if (user.regions === 'region') {
                userGeographicalAssignment.userGeographicalAssignmentType = UserGeographicalAssignmentTypes.Region;
                userGeographicalAssignment.regionId = user.region;
            } else {
                userGeographicalAssignment.userGeographicalAssignmentType = UserGeographicalAssignmentTypes.Global;
            }

            body.geographicalAssignment = userGeographicalAssignment;
        }
        if (user.isResendWelcomeEmail) {
            body.isResendWelcomeEmail = user.isResendWelcomeEmail ;
            body.creatorEmailId = this.userSessionRepository.getUserSession()!.email;
        }

        return body;
    }

    /**
     * Checks if there is a user with the specified email in the system.
     * @param {string} email
     * @returns {Observable<any>}
     */
    checkIfUserExistsByEmail(email: string) {
        email = encodeURIComponent(email);
        return this.httpRepository.get(`${GlobalConfig.endpoints.userExists}?email=${email}`);
    }

    checkAdminToken(): Observable<any> {
        // Removing auto login feature
        return of(true);
        // return this.httpRepository.get(`${GlobalConfig.endpoints.checkAdminToken}`);
    }

    getRoles(type: string|null = null): Observable<Array<UserRole>> {
        let endpoint = GlobalConfig.endpoints.roles;
        if (type) {
            endpoint = endpoint + '?type=' + type.toUpperCase();
        }

        return this.httpRepository.get(endpoint);
    }

    getUser(id: number | undefined): Observable<User> {
        return this.httpRepository.get(`${GlobalConfig.endpoints.users}/${id}`);
    }

    getUsers(type: string): Observable<Array<User>> {
        if (type === 'subuser') {
            type = 'external';
        } else if (type === 'see-user') {
            type = 'see';
        }

        let endpoint = GlobalConfig.endpoints.users;
        if (type) {
            endpoint = endpoint + '?type=' + type;
        }

        return this.httpRepository.get(endpoint);
    }

    getUsersWithPaging(type: string, pagingRequest: PagingRequest): Observable<PagedData> {
        const newPagingRequest = PagingRequest.clone(pagingRequest);

        const filter = new Filter();
        filter.property = 'isSee';
        filter.filterType = 'EQUAL';

        if(type === 'subuser') {
            filter.filterValue = 'false';
        }
        else {
            filter.filterValue = 'true';
        }

        newPagingRequest.filters.push(filter);

        return this.httpRepository.post(`${GlobalConfig.endpoints.users}/paging`, newPagingRequest);
    }

    getUsersByClientWithPaging(clientId: string, pagingRequest: PagingRequest): Observable<PagedData> {
        const newPagingRequest = PagingRequest.clone(pagingRequest);

        const filter = new Filter();
        filter.property = 'company.companyId';
        filter.filterType = 'EQUAL';
        filter.filterValue = clientId;

        newPagingRequest.filters.push(filter);

        return this.httpRepository.post(`${GlobalConfig.endpoints.users}/paging`, newPagingRequest);
    }

    getAllUsers(): Observable<Array<User>> {
        let observables: Array<Observable<Array<User>>> = [];
        observables.push(this.getUsers('subuser'));
        observables.push(this.getUsers('see-user'));

        return observableForkJoin(observables).pipe(
            switchMap((data: Array<Array<User>>) => {
                let users: Array<User> = [];
                if (data) {
                    data.forEach((usersData: Array<User>) => {
                        if (usersData) {
                            usersData.forEach((userData: User) => {
                                users.push(userData);
                            });
                        }
                    });
                }

                return observableOf(users);
            }));
    }

    /**
     * If current user is subuser, then get all subusers under the same client. If not, then get all subusers.
     * @returns {Observable<Array<User}
     */
    getSubusersByCurrentUser(): Observable<Array<User>> {
        let currentUser = this.userSessionRepository.getUserSession();

        if (currentUser!.userType === 'see') {
            return this.getUsers('subuser');
        } else {
            return this.getUser(currentUser!.userId!).pipe(
                switchMap((user: User) => {
                    return this.getUsersByClient(User.getCompanyId(user)!);
                }));
        }
    }

    /**
     * Get all users who can see the specified client. This includes all subusers of the client and all
     * SEE users.
     * @param clientId
     */
    getAllUsersWithAccessToClient(clientId: any): Observable<Array<User>> {
        let observables: Array<Observable<Array<User>>> = [];
        observables.push(this.getUsersByClient(clientId));
        observables.push(this.getUsers('see-user'));

        return observableForkJoin(observables).pipe(
            switchMap((data: Array<Array<User>>) => {
                let users: Array<User> = [];
                if(data) {
                    data.forEach((usersData: Array<User>) => {
                        if(usersData) {
                            usersData.forEach((userData) => {
                                users.push(userData);
                            });
                        }
                    });
                }

                return observableOf(users);
            })
        );
    }

    /**
     * Get all subusers of the specified client.
     * @param clientId
     * @returns {Observable<Array<User>}
     */
    getUsersByClient(clientId: number): Observable<Array<User>> {
        return this.httpRepository.get(`${GlobalConfig.endpoints.clients}/${clientId}/users`);
    }

    /**
     * Create new user
     * @param body
     * @param {string} type 'see' or 'external'
     * @returns {Observable<any>}
     */
    addUser(body: User, type: string) {
        return this.httpRepository.post(`${GlobalConfig.endpoints.users}?type=${type}`, body);
    }

    /**
     * Update existing user.
     * @param body
     * @param userId
     * @returns {Observable<any>}
     */
    updateUser(body: User, userId: any) {
        return this.httpRepository.put(`${GlobalConfig.endpoints.users}/${userId}`, body);
    }

  /*  /!**
     * Deactivate user.
     * @param userId
     * @param isExternalUser
     * @returns {Observable<any>}
     *!/ -> Removed as part of Soft delete
    deactivateUser(userId, isExternalUser: boolean) { // Will no longer be used after the hard delete feature implementation
        return this.changeUserState(userId, false, isExternalUser);
    }*/

    deleteUser(userId: any) {
        return this.httpRepository.delete(`${GlobalConfig.endpoints.users}/${userId}`);
    }

 /*   /!**
     * Activate user.
     * @param userId
     * @param isExternalUser
     * @returns {Observable<any>}
     *!/  -> REMOVED AS A PART OF SOFT DELETE STORY
    activateUser(userId, isExternalUser: boolean) {
        return this.changeUserState(userId, true, isExternalUser);
    }*/

    private changeUserState(userId: any, activateUser: boolean, isExternalUser: boolean): Observable<any> {
        let customHeaders = {'CP-EXTERNAL-USER': isExternalUser ? 'true' : 'false'};
        let state = activateUser ? 'true' : 'false';
        return this.httpRepository.put(`${GlobalConfig.endpoints.users}/${userId}/state?isactive=${state}`, '', customHeaders);
    }

    updateUserLineBarConfigs(userId: number, payload: Array<LineBarAccessConfig>) {
        return this.httpRepository.put(`${GlobalConfig.endpoints.users}/${userId}/linebarconfigs`, payload);
    }

}
